import React, { useEffect, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "./Navbar";
import moment from "moment";
import "./Lottoresult.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Header from "./header";
import ApiConfig from "../../common/config/ApiConfig";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AddResultModal from "../Modals/AddResultModal";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CalculateIcon from "@mui/icons-material/Calculate";
import { useLanguage } from "../../common/i18n/LanguageContext";

const renderResultNumber = (value, currentResult) => {
  if (value == null || value === "null") return "-";
  if (value === "true") {
    return (
      <span className="inline-flex items-center gap-1">
        {currentResult}
        <CheckCircleIcon className="text-green-600" />
      </span>
    );
  }
  if (value === "false") {
    return (
      <span className="inline-flex items-center gap-1">
        {currentResult}
        <CancelIcon className="text-red-600" />
      </span>
    );
  }
  if (value === "calculate") return <CalculateIcon className="text-blue-600" />;
  return value;
};

function Lottoresult() {
  const { t } = useLanguage();
  const [result, setResult] = useState([]);
  const [lottoTypes, setLottoTypes] = useState([]);
  const [rewardTypeGroups, setRewardTypeGroups] = useState([]);
  const [lottotype, setLottotype] = useState("th_gov");
  const [startDate, setStartDate] = useState(new Date());
  const [round, setRound] = useState(1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [sourceStatus, setSourceStatus] = useState([]);
  const [isCheckingSources, setIsCheckingSources] = useState(false);

  // Pings the external result APIs the cron jobs fetch from (Thai/Lao/
  // Hanoi) — see GET /result/sources/status.
  const checkSources = () => {
    setIsCheckingSources(true);
    ApiConfig.get("/result/sources/status")
      .then((result) => {
        if (result.resCode !== 200) {
          toast.error(result.message || t("common_load_error"), {
            position: "top-right",
            autoClose: 3000,
          });
          return;
        }
        setSourceStatus(Array.isArray(result.data) ? result.data : []);
      })
      .catch(() => {
        toast.error(t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
      })
      .finally(() => setIsCheckingSources(false));
  };

  const fetchLottoTypes = () => {
    ApiConfig.get("/getLottoTypes", {}).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setLottoTypes(Array.isArray(result.data) ? result.data : []);
    });
  };

  // Which prize rows to show per lotto type comes from the reward-type
  // catalog itself (groups → rewardTypes, each with the exact `code` that
  // /getresultbylottotype's rows use as `type`), not a hardcoded list — so
  // it stays correct as the catalog changes.
  const fetchRewardTypeGroups = () => {
    ApiConfig.get("/getLottoRewardTypeGroups").then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setRewardTypeGroups(Array.isArray(result.data) ? result.data : []);
    });
  };

  useEffect(() => {
    fetchLottoTypes();
    fetchRewardTypeGroups();
    checkSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const match = lottoTypes.find((lt) => lt.type === lottotype);
    const firstRound = match?.rounds?.[0]?.round;
    if (firstRound != null) setRound(firstRound);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lottotype, lottoTypes]);

  useEffect(() => {
    ResGet();
    // eslint-disable-next-line
  }, [startDate, lottotype, round]);

  const ResGet = () => {
    const params = new URLSearchParams({
      date: moment(startDate).format("YYYY-MM-DD"),
      lottotype: lottotype,
      round: Number(round),
    });

    ApiConfig.get(`/getresultbylottotype?${params.toString()}`).then(
      (result) => {
        if (result.resCode !== 200) {
          toast.error(result.message || t("common_load_error"), {
            position: "top-right",
            autoClose: 3000,
          });
          return;
        }
        setResult(result.data);
      },
    );
  };

  const activeGroups = rewardTypeGroups.filter(
    (g) => g.lottoType?.type === lottotype && g.status === true,
  );

  const selectedLottoType = lottoTypes.find((lt) => lt.type === lottotype);
  const roundOptions = selectedLottoType?.rounds || [];
  const pos = localStorage.getItem("position");

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="shrink-0">
        <Navbar />
      </div>
      <div className="flex flex-1 overflow-hidden items-start">
        <div className="shrink-0 overflow-y-auto h-full">
          <Sidebar pos={pos} />
        </div>
        <div className="flex flex-col flex-1 max-h-full overflow-hidden">
          <div className="shrink-0">
            <Header title={t("menu_lotto_result")} />
          </div>
          <div className="flex-1 overflow-y-auto text-[14px]">
            <div className="md:flex flex-col p-2 w-[100%] bg-brand-blue-200 rounded">
              <div className="flex flex-row w-[100%] items-center justify-between">
                <div className="flex flex-row items-center">
                  <h3 className="text-[14px]">
                    {t("lottoresult_select_type_label")}
                  </h3>
                  <select
                    className="ml-2"
                    value={lottotype}
                    onInput={(e) => setLottotype(e.target.value)}
                  >
                    {lottoTypes.map((lt) => (
                      <option key={lt.id} value={lt.type}>
                        {lt.name}
                      </option>
                    ))}
                  </select>

                  <span className="text-black ml-2">
                    {t("lottoresult_date_label")}
                  </span>
                  <DatePicker
                    className="ml-3"
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                  />
                  <h3 className="ml-4 text-[14px]">
                    {t("lottoresult_select_round_label")}
                  </h3>
                  <select
                    className="ml-2"
                    value={round}
                    onInput={(e) => setRound(e.target.value)}
                  >
                    {roundOptions.map((r) => (
                      <option key={r.id} value={r.round}>
                        {r.round}
                      </option>
                    ))}
                  </select>
                </div>
                {pos === "owner" && (
                  <button
                    type="button"
                    onClick={() => setIsAddModalOpen(true)}
                    className="h-[2.2rem] px-4 bg-gradient-to-b from-brand-gold-300 via-brand-gold-400 to-brand-gold-600 text-brand-ink-onGold border border-brand-gold-200 shadow-gold hover:brightness-105 text-[14px] font-bold rounded"
                  >
                    {t("lottoresult_add_button")}
                  </button>
                )}
              </div>
              <div className="w-[1080px] md:w-full mt-2 h-1 bg-amber-400 border-solid"></div>
            </div>
            <div className="p-2">
              <div className="flex flex-row items-center gap-3 mb-2">
                <h3 className="text-[14px] font-bold text-brand-blue-700">
                  {t("lottoresult_source_title")}
                </h3>
                <button
                  type="button"
                  onClick={checkSources}
                  disabled={isCheckingSources}
                  className="h-[2rem] px-3 bg-brand-blue-600 text-white text-[13px] font-bold rounded hover:brightness-110 disabled:opacity-60"
                >
                  {isCheckingSources
                    ? t("lottoresult_source_checking")
                    : t("lottoresult_source_check_button")}
                </button>
              </div>
              <div className="flex flex-row flex-wrap gap-2">
                {sourceStatus.map((source) => (
                  <div
                    key={source.key}
                    className={`min-w-[240px] max-w-[360px] border rounded px-3 py-2 ${
                      source.ok
                        ? "border-green-400 bg-green-50"
                        : "border-red-400 bg-red-50"
                    }`}
                  >
                    <div className="flex items-center gap-1 font-bold">
                      {source.ok ? (
                        <CheckCircleIcon
                          className="text-green-600"
                          fontSize="small"
                        />
                      ) : (
                        <CancelIcon className="text-red-600" fontSize="small" />
                      )}
                      {t(`lottoresult_source_${source.key}`)}
                      <span
                        className={`ml-auto text-[12px] ${
                          source.ok ? "text-green-700" : "text-red-700"
                        }`}
                      >
                        {source.ok
                          ? t("lottoresult_source_connected")
                          : t("lottoresult_source_failed")}
                      </span>
                    </div>
                    <div className="text-[12px] text-gray-600 mt-1">
                      {source.message} · {source.latencyMs} ms
                    </div>
                    {source.detail && (
                      <div className="text-[12px] text-gray-700">
                        {t("lottoresult_source_latest")}: {source.detail}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <table className="w-1/2 border-collapse border border-gray-300">
              <tbody>
                <tr>
                  <td
                    colSpan="2"
                    className="border purple-gray-300 bg-brand-blue-200 px-4 py-2 text-center"
                  >
                    {t("lottoresult_table_header")}
                  </td>
                </tr>
                {activeGroups.map((group) => (
                  <React.Fragment key={group.id}>
                    <tr>
                      <td
                        colSpan={2}
                        className="border border-gray-300 bg-brand-blue-100 px-4 py-1 text-left font-bold text-brand-blue-700"
                      >
                        {group.name}
                      </td>
                    </tr>
                    {(group.rewardTypes || []).map((rt) => {
                      const rows = result.filter(
                        (item) => item.type === rt.code,
                      );
                      return (
                        <tr key={rt.id}>
                          <td className="border border-gray-300 bg-gray-200 px-4 py-2 text-center">
                            {rt.name}
                          </td>
                          <td className="border border-gray-300 bg-gray-200 px-4 py-2 text-center text-brand-blue-600">
                            <div className="flex flex-wrap justify-center gap-x-2">
                              {rows.map((row, index) => (
                                <span key={index} className="font-bold">
                                  {renderResultNumber(
                                    row.result_number,
                                    row.current_result,
                                  )}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <AddResultModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={ResGet}
        lottotype={lottotype}
      />
    </div>
  );
}

export default Lottoresult;
